import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Clock,
  Columns3,
  List,
  ListTodo,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  UsersRound,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";

import {
  useDeleteProject,
  useProject,
  useProjectStats,
  useProjectTasks,
  useSetProjectStatus,
  useSetTaskStatus,
  type Project,
  type Task,
  type TaskStatus,
  useVacations,
} from "@/api/queries";
import { ApiError } from "@/api/client";
import { useMe } from "@/app/auth";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { EmptyState } from "@/components/common/EmptyState";
import { StatCard } from "@/components/common/StatCard";
import { UserAvatar } from "@/components/common/UserAvatar";
import { VacationBadge } from "@/components/common/VacationBadge";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input, NativeSelect } from "@/components/ui/input";
import { Segmented } from "@/components/ui/segmented";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { errorText } from "@/lib/errors";
import { dateInZone, formatDuration } from "@/lib/format";

import { BudgetBar } from "./BudgetBar";
import { compareTasks } from "./lib";
import { ProjectDialog, ProjectMembersDialog } from "./ProjectDialogs";
import { TaskBoard } from "./TaskBoard";
import { TaskDialog } from "./TaskDialog";
import { TaskTable } from "./TaskTable";

type Action =
  | { kind: "edit" | "members" | "archive" | "delete" }
  | { kind: "task"; taskId?: string; status?: TaskStatus };

export function ProjectPage() {
  const { t } = useTranslation();
  const { projectId = "" } = useParams();
  const project = useProject(projectId);
  const restore = useSetProjectStatus();
  const [action, setAction] = useState<Action | null>(null);

  if (project.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-28" />
        <Skeleton className="h-80" />
      </div>
    );
  }
  if (project.isError) {
    const missing = project.error instanceof ApiError && project.error.status === 404;
    return (
      <>
        <BackLink />
        <Card>
          <EmptyState icon={ListTodo} title={missing ? t("projects.notFound") : t("common.error")} />
        </Card>
      </>
    );
  }

  const p = project.data;
  const close = () => setAction(null);
  const onAction = (a: Action) => {
    if (a.kind === "archive" && p.status === "archived") {
      // Restoring needs no confirmation.
      restore.mutate({ projectId: p.id, status: "active" });
    } else {
      setAction(a);
    }
  };
  return (
    <>
      <BackLink />
      <ProjectHeader project={p} onAction={onAction} />
      {restore.error && (
        <Alert variant="danger" className="mb-5">
          {errorText(t, restore.error)}
        </Alert>
      )}
      {p.status === "archived" && (
        <Alert variant="warning" className="mb-5">
          {t("projects.archivedNotice")}
        </Alert>
      )}
      <ProjectSummary project={p} />

      <Tabs defaultValue="tasks" className="mt-6">
        <TabsList>
          <TabsTrigger value="tasks">
            <ListTodo aria-hidden="true" /> {t("projects.tabs.tasks")}
          </TabsTrigger>
          <TabsTrigger value="members">
            <UsersRound aria-hidden="true" /> {t("projects.tabs.members")}
            <span className="text-xs text-muted tabular-nums">{p.members.length}</span>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="tasks">
          <ProjectTasks project={p} onAction={onAction} />
        </TabsContent>
        <TabsContent value="members">
          <ProjectMembers project={p} onEdit={() => setAction({ kind: "members" })} />
        </TabsContent>
      </Tabs>

      <ProjectActionDialogs project={p} action={action} onClose={close} />
    </>
  );
}

function BackLink() {
  const { t } = useTranslation();
  return (
    <Link to="/projects" className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted hover:text-fg">
      <ArrowLeft className="size-4" aria-hidden="true" /> {t("projects.back")}
    </Link>
  );
}

function ProjectHeader({ project: p, onAction }: { project: Project; onAction: (a: Action) => void }) {
  const { t } = useTranslation();
  const active = p.status === "active";
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-3">
          <span
            className="size-3.5 shrink-0 rounded-full"
            style={{ backgroundColor: p.color }}
            aria-hidden="true"
          />
          <h1 className="min-w-0 text-2xl font-semibold tracking-tight break-words">{p.name}</h1>
          {!active && <Badge className="shrink-0">{t("projects.archived")}</Badge>}
        </div>
        {p.description && <p className="mt-1 max-w-3xl whitespace-pre-wrap text-muted">{p.description}</p>}
      </div>
      {p.can_manage && (
        <div className="flex items-center gap-2">
          {active && (
            <Button onClick={() => onAction({ kind: "task" })}>
              <Plus /> {t("tasks.create")}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="icon" aria-label={`${t("common.actions")}: ${p.name}`}>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onAction({ kind: "edit" })}>
                <Pencil /> {t("projects.edit")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onAction({ kind: "members" })}>
                <UsersRound /> {t("projects.editMembers")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onAction({ kind: "archive" })}>
                {active ? <Archive /> : <ArchiveRestore />}
                {active ? t("projects.archive") : t("projects.restore")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-danger" onSelect={() => onAction({ kind: "delete" })}>
                <Trash2 /> {t("projects.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}

function ProjectSummary({ project: p }: { project: Project }) {
  const { t } = useTranslation();
  const open = p.task_counts.todo + p.task_counts.in_progress;
  const managers = p.members.filter((m) => m.is_manager).map((m) => m.full_name);
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <Card className="flex flex-col gap-3 p-5">
        <span className="flex items-center justify-between font-medium text-muted">
          {t("projects.budget")}
          <span className="flex size-8 items-center justify-center rounded-lg bg-primary-soft text-primary">
            <Clock className="size-4" aria-hidden="true" />
          </span>
        </span>
        <BudgetBar project={p} />
      </Card>
      <StatCard
        label={t("projects.openTasks")}
        icon={ListTodo}
        value={open}
        hint={`${t("tasks.status.in_progress")}: ${p.task_counts.in_progress} · ${t("projects.tasksDone", { count: p.task_counts.done })}`}
      />
      <StatCard
        label={t("projects.members")}
        icon={UsersRound}
        value={p.members.length}
        hint={
          managers.length > 0 ? `${t("projects.managers")}: ${managers.join(", ")}` : t("projects.noManager")
        }
      />
    </div>
  );
}

type View = "board" | "list";
type AssigneeFilter = "all" | "mine" | "none" | string;
const viewKey = "mystaff.tasks.view";

function loadView(): View {
  try {
    return localStorage.getItem(viewKey) === "list" ? "list" : "board";
  } catch {
    return "board";
  }
}

function ProjectTasks({ project: p, onAction }: { project: Project; onAction: (a: Action) => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const tasks = useProjectTasks(p.id);
  const move = useSetTaskStatus();
  const [view, setView] = useState<View>(loadView);
  const [query, setQuery] = useState("");
  const [assignee, setAssignee] = useState<AssigneeFilter>("all");
  const [hideDone, setHideDone] = useState(false);
  const today = dateInZone(new Date(), me.user.timezone);
  const active = p.status === "active";
  const names = useMemo(() => new Map(p.members.map((m) => [m.user_id, m.full_name])), [p.members]);

  const changeView = (v: View) => {
    setView(v);
    try {
      localStorage.setItem(viewKey, v);
    } catch {
      // Private mode: the choice lasts until reload.
    }
  };
  const canMove = (task: Task) => active && (p.can_manage || task.assignee_id === me.user.id);
  const onMove = (task: Task, status: TaskStatus) => move.mutate({ taskId: task.id, status });

  const all = tasks.data?.items ?? [];
  const q = query.trim().toLowerCase();
  const visible = all
    .filter((task) => !q || task.title.toLowerCase().includes(q))
    .filter((task) =>
      assignee === "all"
        ? true
        : assignee === "mine"
          ? task.assignee_id === me.user.id
          : assignee === "none"
            ? !task.assignee_id
            : task.assignee_id === assignee,
    )
    .filter((task) => view === "board" || !hideDone || task.status !== "done")
    .sort(compareTasks);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Segmented
          aria-label={t("tasks.view.label")}
          value={view}
          onChange={changeView}
          options={[
            { value: "board", label: t("tasks.view.board"), icon: <Columns3 aria-hidden="true" /> },
            { value: "list", label: t("tasks.view.list"), icon: <List aria-hidden="true" /> },
          ]}
        />
        <div className="relative w-full max-w-xs">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
            aria-hidden="true"
          />
          <Input
            type="search"
            aria-label={t("tasks.search")}
            placeholder={t("tasks.search")}
            className="h-9 pl-9"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <NativeSelect
          aria-label={t("tasks.filters.assignee")}
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
        >
          <option value="all">{`${t("tasks.filters.assignee")}: ${t("tasks.filters.all")}`}</option>
          <option value="mine">{t("tasks.filters.mine")}</option>
          <option value="none">{t("tasks.filters.unassigned")}</option>
          {p.members
            .filter((m) => m.user_id !== me.user.id)
            .map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.full_name}
              </option>
            ))}
        </NativeSelect>
        {view === "list" && (
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={hideDone} onCheckedChange={setHideDone} />
            {t("tasks.hideDone")}
          </label>
        )}
      </div>

      {move.error && <Alert variant="danger">{errorText(t, move.error)}</Alert>}

      {tasks.isPending ? (
        <Skeleton className="h-64" />
      ) : tasks.isError ? (
        <Alert variant="danger">{errorText(t, tasks.error)}</Alert>
      ) : all.length === 0 ? (
        <Card>
          <EmptyState
            icon={ListTodo}
            title={t("tasks.empty")}
            description={p.can_manage && active ? t("tasks.emptyManage") : undefined}
            action={
              p.can_manage &&
              active && (
                <Button onClick={() => onAction({ kind: "task" })}>
                  <Plus /> {t("tasks.create")}
                </Button>
              )
            }
          />
        </Card>
      ) : view === "board" ? (
        <TaskBoard
          tasks={visible}
          names={names}
          today={today}
          canCreate={p.can_manage && active}
          canMove={canMove}
          onOpen={(task) => onAction({ kind: "task", taskId: task.id })}
          onCreate={(status) => onAction({ kind: "task", status })}
          onMove={onMove}
        />
      ) : visible.length === 0 ? (
        <Card>
          <EmptyState icon={ListTodo} title={t("tasks.emptyFiltered")} />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <TaskTable
            tasks={visible}
            today={today}
            names={names}
            canMove={canMove}
            onOpen={(task) => onAction({ kind: "task", taskId: task.id })}
            onMove={onMove}
          />
        </Card>
      )}
    </div>
  );
}

function ProjectMembers({ project: p, onEdit }: { project: Project; onEdit: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const me = useMe();
  const stats = useProjectStats(p.id);
  const time = new Map((stats.data?.by_user ?? []).map((u) => [u.user_id, u.tracked_seconds]));
  const visibleTime = new Set((stats.data?.by_user ?? []).map((u) => u.user_id));
  const vacations = new Map((useVacations().data ?? []).map((v) => [v.user.id, v.vacation]));

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
        <p className="text-sm text-muted">{t("projects.memberTimeHint")}</p>
        {p.can_manage && (
          <Button variant="secondary" onClick={onEdit}>
            <UsersRound /> {t("projects.editMembers")}
          </Button>
        )}
      </div>
      {p.members.length === 0 ? (
        <EmptyState icon={UsersRound} title={t("common.noResults")} />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>{t("projects.members")}</TH>
              <TH>{t("projects.role")}</TH>
              <TH className="text-right">{t("projects.memberTime")}</TH>
            </tr>
          </THead>
          <tbody>
            {p.members.map((m) => (
              <TR key={m.user_id}>
                <TD>
                  <span className="flex items-center gap-2.5">
                    <UserAvatar name={m.full_name} className="size-7" />
                    <span className="font-medium">
                      {m.full_name}
                      {m.user_id === me.user.id && (
                        <span className="ml-1.5 text-muted">({t("common.you")})</span>
                      )}
                    </span>
                    <VacationBadge vacation={vacations.get(m.user_id)} upcoming />
                  </span>
                </TD>
                <TD>{m.is_manager ? <Badge variant="primary">{t("projects.manager")}</Badge> : null}</TD>
                <TD className="text-right tabular-nums">
                  {visibleTime.has(m.user_id) || m.user_id === me.user.id
                    ? formatDuration(time.get(m.user_id) ?? 0, lang)
                    : "—"}
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function ProjectActionDialogs({
  project: p,
  action,
  onClose,
}: {
  project: Project;
  action: Action | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const tasks = useProjectTasks(p.id);
  const setStatus = useSetProjectStatus();
  const remove = useDeleteProject();
  if (!action) return null;

  switch (action.kind) {
    case "edit":
      return <ProjectDialog project={p} onClose={onClose} />;
    case "members":
      return <ProjectMembersDialog project={p} onClose={onClose} />;
    case "archive":
      return (
        <ConfirmDialog
          title={t("projects.archiveTitle", { name: p.name })}
          description={t("projects.archiveText")}
          confirmLabel={t("projects.archive")}
          errorText={(err) => errorText(t, err)}
          onConfirm={() => setStatus.mutateAsync({ projectId: p.id, status: "archived" })}
          onClose={onClose}
        />
      );
    case "delete":
      return (
        <ConfirmDialog
          danger
          title={t("projects.deleteTitle", { name: p.name })}
          description={t("projects.deleteText")}
          confirmLabel={t("common.delete")}
          errorText={(err) => errorText(t, err)}
          onConfirm={async () => {
            await remove.mutateAsync(p.id);
            void navigate("/projects", { replace: true });
          }}
          onClose={onClose}
        />
      );
    case "task": {
      const task = action.taskId ? tasks.data?.items.find((x) => x.id === action.taskId) : undefined;
      if (action.taskId && !task) return null;
      return (
        <TaskDialog
          project={p}
          task={task}
          initialStatus={action.status}
          canMove={p.can_manage || (task?.assignee_id !== undefined && task.assignee_id === me.user.id)}
          onClose={onClose}
        />
      );
    }
  }
}
